import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  ButtonGroup,
  Stack,
  Box,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material';
import { SubtitleInline, SectionTitle } from 'components/shared/Typography';
import { generateClient } from 'aws-amplify/api';

const API = generateClient();

const ANTHROPIC_MODELS = {
  CLAUDE_3_5_HAIKU_20241022: {
    label: 'Claude 3.5 Haiku',
    description: 'Fast, accurate, cost-effective (RECOMMENDED FOR ACORD25)',
    cost: '$0.80/$4 per 1M tokens',
    costPerCert: '~$0.007/cert',
    recommended: true,
  },
  CLAUDE_3_5_SONNET_20241022: {
    label: 'Claude 3.5 Sonnet',
    description: 'Best balance of intelligence and speed',
    cost: '$3/$15 per 1M tokens',
    costPerCert: '~$0.03/cert',
  },
  CLAUDE_3_OPUS_20240229: {
    label: 'Claude 3 Opus',
    description: 'Does not support PDF input',
    cost: '$15/$75 per 1M tokens',
    disabled: true,
  },
  CLAUDE_3_HAIKU_20240307: {
    label: 'Claude 3 Haiku (Legacy)',
    description: 'Older version - use 3.5 Haiku instead',
    cost: '$0.25/$1.25 per 1M tokens',
    deprecated: true,
  },
};

const EXTRACTION_STRATEGIES = {
  CLAUDE_PRIMARY: {
    label: 'Claude Primary + OCR Fallback',
    description: 'Use Claude if confidence meets threshold, otherwise use OCR',
  },
  OCR_PRIMARY: {
    label: 'OCR Primary + Claude Validation',
    description: 'Use OCR primarily, validate with Claude if OCR confidence is low',
  },
  PARALLEL: {
    label: 'Parallel Comparison',
    description: 'Run both methods and use whichever has higher confidence',
  },
  HYBRID: {
    label: 'Hybrid (Intelligent Merge)',
    description: 'Merge best results: Claude for currency limits, OCR for dates/text',
    recommended: true,
  },
};

export default function AnthropicAIConfiguration({ organization = {} }) {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [message, setMessage] = useState(null);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [defaultModel, setDefaultModel] = useState('CLAUDE_3_5_HAIKU_20241022');
  const [extractionStrategy, setExtractionStrategy] = useState('HYBRID');
  const [fallbackToOCR, setFallbackToOCR] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.85);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [temperature, setTemperature] = useState(0.0);
  const [enableFieldConfidence, setEnableFieldConfidence] = useState(true);

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing settings
  useEffect(() => {
    if (organization?.id) {
      fetchSettings();
    }
  }, [organization?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const query = `
        query AnthropicSettingsByOrganization($organizationID: ID!) {
          anthropicSettingsByOrganization(
            organizationID: $organizationID
            filter: { clientID: { attributeExists: false } }
            limit: 1
          ) {
            items {
              id
              organizationID
              enabled
              defaultModel
              extractionStrategy
              fallbackToOCR
              confidenceThreshold
              maxTokens
              temperature
              enableFieldConfidence
              createdAt
              updatedAt
            }
          }
        }
      `;

      const result = await API.graphql({
        query,
        variables: { organizationID: organization.id },
      });

      const items = result?.data?.anthropicSettingsByOrganization?.items || [];

      if (items.length > 0) {
        const existingSettings = items[0];
        setSettings(existingSettings);
        setEnabled(existingSettings.enabled || false);
        setDefaultModel(existingSettings.defaultModel || 'CLAUDE_3_5_HAIKU_20241022');
        setExtractionStrategy(existingSettings.extractionStrategy || 'HYBRID');
        setFallbackToOCR(existingSettings.fallbackToOCR !== false);
        setConfidenceThreshold(existingSettings.confidenceThreshold || 0.85);
        setMaxTokens(existingSettings.maxTokens || 4096);
        setTemperature(existingSettings.temperature || 0.0);
        setEnableFieldConfidence(existingSettings.enableFieldConfidence !== false);
      }
    } catch (error) {
      console.error('Error fetching Anthropic settings:', error);
      setMessage({ severity: 'error', text: 'Failed to load AI settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const input = {
        organizationID: organization.id,
        enabled,
        defaultModel,
        extractionStrategy,
        fallbackToOCR,
        confidenceThreshold,
        maxTokens,
        temperature,
        enableFieldConfidence,
      };

      if (settings?.id) {
        // Update existing settings
        const updateMutation = /* GraphQL */ `
          mutation UpdateANTHROPICSETTINGS($input: UpdateANTHROPICSETTINGSInput!) {
            updateANTHROPICSETTINGS(input: $input) {
              id
              organizationID
              enabled
              defaultModel
              extractionStrategy
              fallbackToOCR
              confidenceThreshold
              maxTokens
              temperature
              enableFieldConfidence
              createdAt
              updatedAt
            }
          }
        `;

        await API.graphql({
          query: updateMutation,
          variables: {
            input: {
              id: settings.id,
              ...input,
            },
          },
        });
      } else {
        // Create new settings
        const createMutation = /* GraphQL */ `
          mutation CreateANTHROPICSETTINGS($input: CreateANTHROPICSETTINGSInput!) {
            createANTHROPICSETTINGS(input: $input) {
              id
              organizationID
              enabled
              defaultModel
              extractionStrategy
              fallbackToOCR
              confidenceThreshold
              maxTokens
              temperature
              enableFieldConfidence
              createdAt
              updatedAt
            }
          }
        `;

        const result = await API.graphql({
          query: createMutation,
          variables: { input },
        });
        setSettings(result.data.createANTHROPICSETTINGS);
      }

      setMessage({ severity: 'success', text: 'AI settings saved successfully!' });
      setEditMode(false);
      setHasChanges(false);
      await fetchSettings();
    } catch (error) {
      console.error('Error saving Anthropic settings:', error);
      setMessage({ severity: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (settings) {
      setEnabled(settings.enabled || false);
      setDefaultModel(settings.defaultModel || 'CLAUDE_3_5_HAIKU_20241022');
      setExtractionStrategy(settings.extractionStrategy || 'HYBRID');
      setFallbackToOCR(settings.fallbackToOCR !== false);
      setConfidenceThreshold(settings.confidenceThreshold || 0.85);
      setMaxTokens(settings.maxTokens || 4096);
      setTemperature(settings.temperature || 0.0);
      setEnableFieldConfidence(settings.enableFieldConfidence !== false);
    }
    setEditMode(false);
    setHasChanges(false);
    setMessage(null);
  };

  if (loading) {
    return (
      <Box>
        <Typography>Loading AI settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle sx={{ mb: 2 }}>
        AI-Powered Certificate Extraction
        {editMode ? (
          <ButtonGroup sx={{ ml: 2 }}>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </ButtonGroup>
        ) : (
          <Button onClick={() => setEditMode(true)}>Edit</Button>
        )}
      </SectionTitle>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={3} direction="column">
        {/* Enable/Disable Claude */}
        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              disabled={!editMode}
              onChange={(e) => {
                setEnabled(e.target.checked);
                setHasChanges(true);
              }}
              color="primary"
            />
          }
          label={
            <Box>
              <SubtitleInline>Enable Claude AI Extraction</SubtitleInline>
              <Typography variant="body2" color="textSecondary">
                Use Claude's vision AI to extract certificate data with higher accuracy
              </Typography>
            </Box>
          }
        />

        {enabled && (
          <>
            <Divider />

            {/* Model Selection */}
            <Box>
              <SubtitleInline>Claude Model</SubtitleInline>
              <Select
                fullWidth
                value={defaultModel}
                disabled={!editMode}
                onChange={(e) => {
                  setDefaultModel(e.target.value);
                  setHasChanges(true);
                }}
                sx={{ mt: 1 }}
              >
                {Object.entries(ANTHROPIC_MODELS).map(([key, model]) => (
                  <MenuItem key={key} value={key} disabled={model.disabled || model.deprecated}>
                    <Box>
                      <Typography variant="body1">
                        {model.label}
                        {model.recommended && (
                          <Chip label="Recommended" size="small" color="success" sx={{ ml: 1 }} />
                        )}
                        {model.disabled && (
                          <Chip label="Unsupported" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                        {model.deprecated && (
                          <Chip label="Legacy" size="small" color="warning" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {model.description}
                        {' • '}{model.cost}
                        {model.costPerCert && ` • ${model.costPerCert}`}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Extraction Strategy */}
            <Box>
              <SubtitleInline>Extraction Strategy</SubtitleInline>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <RadioGroup
                  value={extractionStrategy}
                  onChange={(e) => {
                    setExtractionStrategy(e.target.value);
                    setHasChanges(true);
                  }}
                >
                  {Object.entries(EXTRACTION_STRATEGIES).map(([key, strategy]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      disabled={!editMode}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">
                            {strategy.label}
                            {strategy.recommended && (
                              <Chip label="Recommended" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {strategy.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Confidence Threshold */}
            <Box>
              <SubtitleInline>Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%</SubtitleInline>
              <Typography variant="caption" color="textSecondary" display="block">
                Minimum confidence required to accept Claude extraction results
              </Typography>
              <Slider
                value={confidenceThreshold}
                min={0.5}
                max={1.0}
                step={0.05}
                disabled={!editMode}
                onChange={(e, value) => {
                  setConfidenceThreshold(value);
                  setHasChanges(true);
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
                sx={{ mt: 2, maxWidth: 400 }}
              />
            </Box>

            {/* Fallback to OCR */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={fallbackToOCR}
                  disabled={!editMode}
                  onChange={(e) => {
                    setFallbackToOCR(e.target.checked);
                    setHasChanges(true);
                  }}
                  color="primary"
                />
              }
              label={
                <Box>
                  <SubtitleInline>Fallback to OCR on Error</SubtitleInline>
                  <Typography variant="body2" color="textSecondary">
                    Use traditional OCR if Claude extraction fails or is unavailable
                  </Typography>
                </Box>
              }
            />

            {/* Advanced Settings */}
            <Divider />
            <Typography variant="subtitle2" color="textSecondary">
              Advanced Settings
            </Typography>

            {/* Max Tokens */}
            <Box>
              <SubtitleInline>Max Tokens: {maxTokens}</SubtitleInline>
              <Typography variant="caption" color="textSecondary" display="block">
                Maximum number of tokens for Claude response (higher = more detailed extraction)
              </Typography>
              <Select
                value={maxTokens}
                disabled={!editMode}
                onChange={(e) => {
                  setMaxTokens(e.target.value);
                  setHasChanges(true);
                }}
                size="small"
                sx={{ mt: 1 }}
              >
                <MenuItem value={2048}>2,048 tokens (Basic)</MenuItem>
                <MenuItem value={4096}>4,096 tokens (Recommended)</MenuItem>
                <MenuItem value={8192}>8,192 tokens (Detailed)</MenuItem>
              </Select>
            </Box>

            {/* Temperature */}
            <Box>
              <SubtitleInline>Temperature: {temperature.toFixed(1)}</SubtitleInline>
              <Typography variant="caption" color="textSecondary" display="block">
                Randomness in extraction (0.0 = deterministic, 1.0 = creative)
              </Typography>
              <Slider
                value={temperature}
                min={0.0}
                max={1.0}
                step={0.1}
                disabled={!editMode}
                onChange={(e, value) => {
                  setTemperature(value);
                  setHasChanges(true);
                }}
                valueLabelDisplay="auto"
                sx={{ mt: 2, maxWidth: 400 }}
              />
            </Box>

            {/* Field Confidence Tracking */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableFieldConfidence}
                  disabled={!editMode}
                  onChange={(e) => {
                    setEnableFieldConfidence(e.target.checked);
                    setHasChanges(true);
                  }}
                  color="primary"
                />
              }
              label={
                <Box>
                  <SubtitleInline>Enable Field-Level Confidence Tracking</SubtitleInline>
                  <Typography variant="body2" color="textSecondary">
                    Track confidence scores for individual fields (may increase processing time)
                  </Typography>
                </Box>
              }
            />
          </>
        )}

        {/* Status */}
        {!enabled && (
          <Alert severity="info">
            Claude AI extraction is currently disabled. Enable it above to improve certificate data extraction accuracy.
          </Alert>
        )}

        {settings?.updatedAt && (
          <Typography variant="caption" color="textSecondary">
            Last updated: {new Date(settings.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
